/**
 * PreferencesModal.tsx - Universal resizable/movable preferences modal
 * 
 * FEATURES:
 * - Resizable and draggable modal window
 * - Text selection only in input fields
 * - Live preview through semi-transparent backdrop
 * - Module-based settings organization
 * - Import/Export functionality
 * - Reset to defaults
 * 
 * USAGE:
 * ```tsx
 * import { PreferencesModal } from '@opcentral/configgr';
 * 
 * <PreferencesModal
 *   modules={MODULES}
 *   onClose={() => setShowSettings(false)}
 *   getSchemaByCategory={getSchemaByCategory}
 *   configManager={configgr}
 * />
 * ```
 */

import { useState, useRef, useEffect } from 'react';
import './PreferencesModal.css';

export interface PreferencesModule {
  id: string;
  name: string;
  icon: string;
  category: string;
}

export interface PreferencesModalProps {
  modules: PreferencesModule[];
  onClose?: () => void;
  getSchemaByCategory: (categoryId: string) => Record<string, any>;
  configManager: {
    getSetting: (key: string) => any;
    updateSetting: (key: string, value: any) => void;
    deleteSetting: (key: string) => void;
  };
  title?: string;
  appId?: string;
  onChange?: (key: string, value: any) => void; // Callback for live updates
  onEditSkin?: (skinId: string) => void; // Callback to open Stylr for skin editing
}

export function PreferencesModal({
  modules,
  onClose,
  getSchemaByCategory,
  configManager,
  title = '⚙️ Settings',
  appId = 'app',
  onChange,
  onEditSkin
}: PreferencesModalProps) {
  const [activeModule, setActiveModule] = useState<string>(modules[0]?.id || 'appearance');
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, any>>({});
  
  // Resizable/Draggable state
  const [size, setSize] = useState({ width: 1000, height: 700 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ width: 0, height: 0, x: 0, y: 0 });
  
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Center modal on mount
  useEffect(() => {
    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      setPosition({
        x: (window.innerWidth - rect.width) / 2,
        y: (window.innerHeight - rect.height) / 2
      });
    }
  }, []);
  
  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.modal-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };
  
  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    } else if (isResizing) {
      const newWidth = Math.max(600, resizeStart.width + (e.clientX - resizeStart.x));
      const newHeight = Math.max(400, resizeStart.height + (e.clientY - resizeStart.y));
      setSize({ width: newWidth, height: newHeight });
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };
  
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart]);
  
  // Resize handler
  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      width: size.width,
      height: size.height,
      x: e.clientX,
      y: e.clientY
    });
  };
  
  // Settings handlers
  const handleChange = (key: string, value: any) => {
    setPendingChanges(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    
    // Apply immediately for live preview
    configManager.updateSetting(`${appId}.${key}`, value);
    
    // Trigger onChange callback for live updates
    if (onChange) {
      onChange(key, value);
    }
  };
  
  const handleSave = () => {
    setHasChanges(false);
    setPendingChanges({});
    
    // Use onChange to notify parent (which can use Promptr)
    if (onChange) {
      onChange('_save', true);
    }
  };
  
  const handleReset = () => {
    if (window.confirm('Reset all settings to defaults?')) {
      // Reset all settings for active module
      const settings = getSchemaByCategory(activeModule);
      Object.keys(settings).forEach(key => {
        configManager.deleteSetting(`${appId}.${key}`);
      });
      setHasChanges(false);
      setPendingChanges({});
    }
  };
  
  const handleExport = () => {
    const allSettings: Record<string, any> = {};
    modules.forEach(module => {
      const settings = getSchemaByCategory(module.id);
      Object.keys(settings).forEach(key => {
        const value = configManager.getSetting(`${appId}.${key}`);
        if (value !== undefined) {
          allSettings[key] = value;
        }
      });
    });
    
    const config = JSON.stringify(allSettings, null, 2);
    const blob = new Blob([config], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${appId}-settings-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            const imported = JSON.parse(content);
            Object.entries(imported).forEach(([key, value]) => {
              configManager.updateSetting(`${appId}.${key}`, value);
            });
            // Notify parent via onChange
            if (onChange) {
              onChange('_import_success', true);
            }
          } catch {
            // Notify parent via onChange
            if (onChange) {
              onChange('_import_error', true);
            }
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };
  
  // Render setting input
  const renderSetting = (key: string, schema: any) => {
    const value = configManager.getSetting(`${appId}.${key}`) ?? schema.default;
    const settingKey = key.split('.').pop() || key;
    const label = schema.label || settingKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    
    if (schema.type === 'boolean') {
      return (
        <div key={key} className="setting-row">
          <div className="setting-info">
            <label className="setting-label">{label}</label>
            {schema.description && <p className="setting-description">{schema.description}</p>}
          </div>
          <input
            type="checkbox"
            checked={value ?? schema.default}
            onChange={(e) => handleChange(key, e.target.checked)}
            className="setting-checkbox"
          />
        </div>
      );
    }
    
    if (schema.enum) {
      const isSkinSetting = key.includes('skin') && schema.label === 'Skin';
      
      return (
        <div key={key} className="setting-row">
          <div className="setting-info">
            <label className="setting-label">{label}</label>
            {schema.description && <p className="setting-description">{schema.description}</p>}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={value ?? schema.default}
              onChange={(e) => handleChange(key, e.target.value)}
              className="setting-select"
            >
              {schema.enum.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {isSkinSetting && onEditSkin && (
              <button
                onClick={() => onEditSkin(value ?? schema.default)}
                className="btn-secondary"
                title="Edit skin in Stylr"
                style={{ whiteSpace: 'nowrap' }}
              >
                🎨 Edit Skin
              </button>
            )}
          </div>
        </div>
      );
    }
    
    if (schema.type === 'number') {
      return (
        <div key={key} className="setting-row">
          <div className="setting-info">
            <label className="setting-label">{label}</label>
            {schema.description && <p className="setting-description">{schema.description}</p>}
          </div>
          <input
            type="number"
            value={value ?? schema.default}
            onChange={(e) => handleChange(key, Number(e.target.value))}
            min={schema.min}
            max={schema.max}
            className="setting-input"
          />
        </div>
      );
    }
    
    return (
      <div key={key} className="setting-row">
        <div className="setting-info">
          <label className="setting-label">{label}</label>
          {schema.description && <p className="setting-description">{schema.description}</p>}
        </div>
        <input
          type="text"
          value={value ?? schema.default}
          onChange={(e) => handleChange(key, e.target.value)}
          className="setting-input"
          placeholder={schema.placeholder}
        />
      </div>
    );
  };
  
  const activeSettings = getSchemaByCategory(activeModule);
  
  return (
    <div className="preferences-modal-overlay" onClick={onClose}>
      <div
        ref={modalRef}
        className="preferences-modal"
        style={{
          width: `${size.width}px`,
          height: `${size.height}px`,
          transform: `translate(${position.x}px, ${position.y}px)`
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={handleMouseDown}
      >
        {/* Header */}
        <div className="modal-header">
          <h2>{title}</h2>
          <div className="header-actions">
            <button onClick={handleExport} className="btn-secondary" title="Export Settings">
              📤
            </button>
            <button onClick={handleImport} className="btn-secondary" title="Import Settings">
              📥
            </button>
            {onClose && (
              <button onClick={onClose} className="btn-close" title="Close">
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="modal-content">
          {/* Module Sidebar */}
          <div className="modal-sidebar">
            <div className="module-list">
              {modules.map(module => (
                <button
                  key={module.id}
                  className={`module-item ${activeModule === module.id ? 'active' : ''}`}
                  onClick={() => setActiveModule(module.id)}
                >
                  <span className="module-icon">{module.icon}</span>
                  <span className="module-name">{module.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Settings Panel */}
          <div className="modal-main">
            <div className="module-header">
              <h3>
                {modules.find(m => m.id === activeModule)?.icon}{' '}
                {modules.find(m => m.id === activeModule)?.name}
              </h3>
            </div>
            
            <div className="settings-list">
              {Object.entries(activeSettings).map(([key, schema]) =>
                renderSetting(key, schema)
              )}
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-left">
            <button onClick={handleReset} className="btn-danger">
              🔄 Reset
            </button>
          </div>
          <div className="footer-right">
            {hasChanges && (
              <span className="changes-indicator">● Unsaved changes</span>
            )}
            <button onClick={handleSave} className="btn-primary" disabled={!hasChanges}>
              💾 Save
            </button>
          </div>
        </div>
        
        {/* Resize Handle */}
        <div
          className="resize-handle"
          onMouseDown={handleResizeStart}
          title="Drag to resize"
        />
      </div>
    </div>
  );
}

export default PreferencesModal;
